import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../components/ConfirmDialog';
import { useToast } from '../../../components/ToastProvider';
import { Edit2, Save, X, Plus, Trash2, Loader2, CalendarRange, Percent, Tag, ShoppingBag, Layers, Check } from 'lucide-react';
import {
  useSeasons,
  useSaveSeason,
  useDeleteSeason,
  useSaveProperty,
  useActiveProperty,
  usePromos,
  useCreatePromo,
  useDeletePromo,
  useExtras,
  useCreateExtra,
  useUpdateExtra,
  useDeleteExtra,
  useBulkProperties,
  type Season,
  type Extra,
  type ExtraPricingType,
  type Property,
  type BulkOp,
} from '../../../hooks/useManagement';
import PropertySelector from '../../../components/PropertySelector';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

type SeasonDraft = {
  name: string;
  start_month: number;
  end_month: number;
  price_per_night: number;
  min_stay: number;
};

type BaseDraft = {
  cleaning_fee: number;
  security_deposit: number;
  check_in_time: string;
  check_out_time: string;
};

export default function ManagePricingPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const savedToast = () => toast.success(t('common.savedToast', 'Cambios guardados'));
  const saveErrToast = () => toast.error(t('common.saveError', 'No se ha podido guardar. Inténtalo de nuevo.'));
  const confirm = useConfirm();
  const [bulkOpen, setBulkOpen] = useState(false);
  const monthName = (m: number): string => t(`portal.manage.pricing.months.${m}`, '');
  const monthRange = (s: Season): string =>
    s.start_month === s.end_month
      ? monthName(s.start_month)
      : `${monthName(s.start_month)} – ${monthName(s.end_month)}`;

  const { propertyId, properties, isLoading: propsLoading } = useActiveProperty(true);
  const hasProperties = properties.length > 0;
  const property = properties.find((p) => p.id === propertyId) ?? properties[0];

  const { data: seasons, isLoading: seasonsLoading } = useSeasons(propertyId);
  const saveSeason = useSaveSeason();
  const deleteSeason = useDeleteSeason();

  const saveProperty = useSaveProperty();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [seasonDraft, setSeasonDraft] = useState<SeasonDraft | null>(null);

  const [editingBase, setEditingBase] = useState(false);
  const [baseDraft, setBaseDraft] = useState<BaseDraft | null>(null);

  // ── Length-of-stay discounts ───────────────────────────────────────────────
  const [editingDiscounts, setEditingDiscounts] = useState(false);
  const [discountDraft, setDiscountDraft] = useState<{ weekly: number; monthly: number } | null>(null);

  // ── Promo codes ────────────────────────────────────────────────────────────
  const { data: promos, isLoading: promosLoading } = usePromos(propertyId);
  const createPromo = useCreatePromo();
  const deletePromo = useDeletePromo();
  const [showPromoForm, setShowPromoForm] = useState(false);
  const emptyPromo = {
    code: '',
    kind: 'percent' as 'percent' | 'fixed',
    amount: '' as string,
    valid_from: '',
    valid_until: '',
    max_uses: '' as string,
    scope: 'property' as 'property' | 'all',
  };
  const [promoDraft, setPromoDraft] = useState(emptyPromo);
  const [promoError, setPromoError] = useState('');

  // ── Paid extras ────────────────────────────────────────────────────────────
  const { data: extras, isLoading: extrasLoading } = useExtras(propertyId);
  const createExtra = useCreateExtra();
  const updateExtra = useUpdateExtra();
  const deleteExtra = useDeleteExtra();
  const emptyExtra = {
    name: '',
    price: '' as string,
    pricing_type: 'per_stay' as ExtraPricingType,
    description: '',
  };
  const [showExtraForm, setShowExtraForm] = useState(false);
  const [editingExtraId, setEditingExtraId] = useState<number | null>(null);
  const [extraDraft, setExtraDraft] = useState(emptyExtra);
  const [extraError, setExtraError] = useState('');

  const pricingTypeLabels: Record<ExtraPricingType, string> = {
    per_stay: t('portal.manage.pricing.extras.perStay', 'Por estancia'),
    per_night: t('portal.manage.pricing.extras.perNight', 'Por noche'),
    per_guest: t('portal.manage.pricing.extras.perGuest', 'Por huésped'),
  };

  // ── Season inline edit ─────────────────────────────────────────────────────
  const startEdit = (s: Season) => {
    setEditingId(s.id);
    setSeasonDraft({
      name: s.name,
      start_month: s.start_month,
      end_month: s.end_month,
      price_per_night: s.price_per_night,
      min_stay: s.min_stay,
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setSeasonDraft(null);
  };
  const saveEdit = (id: number) => {
    if (!seasonDraft) return;
    saveSeason.mutate({ id, ...seasonDraft });
    cancelEdit();
  };

  const addSeason = () => {
    const order = (seasons?.length ?? 0) + 1;
    saveSeason.mutate({
      name: t('portal.manage.pricing.newSeason'),
      start_month: 1,
      end_month: 1,
      price_per_night: property?.base_price ?? 100,
      min_stay: 2,
      sort_order: order,
      property_id: propertyId ?? undefined,
    });
  };

  // ── Base settings ──────────────────────────────────────────────────────────
  const startBaseEdit = () => {
    setBaseDraft({
      cleaning_fee: property?.cleaning_fee ?? 0,
      security_deposit: property?.security_deposit ?? 0,
      check_in_time: property?.check_in_time ?? '15:00',
      check_out_time: property?.check_out_time ?? '11:00',
    });
    setEditingBase(true);
  };
  const cancelBaseEdit = () => {
    setEditingBase(false);
    setBaseDraft(null);
  };
  const saveBase = () => {
    if (!property || !baseDraft) return;
    saveProperty.mutate({ id: property.id, ...baseDraft }, { onSuccess: savedToast, onError: saveErrToast });
    cancelBaseEdit();
  };

  // ── Length-of-stay discounts ───────────────────────────────────────────────
  const startDiscountEdit = () => {
    setDiscountDraft({
      weekly: property?.discount_weekly_percent ?? 0,
      monthly: property?.discount_monthly_percent ?? 0,
    });
    setEditingDiscounts(true);
  };
  const cancelDiscountEdit = () => {
    setEditingDiscounts(false);
    setDiscountDraft(null);
  };
  const saveDiscounts = () => {
    if (!property || !discountDraft) return;
    saveProperty.mutate({
      id: property.id,
      discount_weekly_percent: Math.max(0, Math.min(100, Math.floor(discountDraft.weekly))) || null,
      discount_monthly_percent: Math.max(0, Math.min(100, Math.floor(discountDraft.monthly))) || null,
    }, { onSuccess: savedToast, onError: saveErrToast });
    cancelDiscountEdit();
  };

  // ── Promo codes ────────────────────────────────────────────────────────────
  const submitPromo = () => {
    setPromoError('');
    const code = promoDraft.code.trim();
    if (!code) { setPromoError(t('portal.manage.pricing.promo.errCode', 'Indica un código.')); return; }
    const amount = Number(promoDraft.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setPromoError(t('portal.manage.pricing.promo.errAmount', 'Indica un descuento mayor que cero.'));
      return;
    }
    createPromo.mutate(
      {
        code,
        discount_percent: promoDraft.kind === 'percent' ? amount : null,
        discount_fixed: promoDraft.kind === 'fixed' ? amount : null,
        valid_from: promoDraft.valid_from || null,
        valid_until: promoDraft.valid_until || null,
        max_uses: promoDraft.max_uses ? Number(promoDraft.max_uses) : null,
        property_id: promoDraft.scope === 'all' ? null : propertyId,
      },
      {
        onSuccess: () => { setPromoDraft(emptyPromo); setShowPromoForm(false); },
        onError: (e) => setPromoError(
          (e as Error).message === 'duplicate_code'
            ? t('portal.manage.pricing.promo.errDuplicate', 'Ya existe un código con ese nombre.')
            : t('portal.manage.pricing.promo.errGeneric', 'No se ha podido crear el código.'),
        ),
      },
    );
  };

  // ── Paid extras ────────────────────────────────────────────────────────────
  const openExtraForm = (x?: Extra) => {
    setExtraError('');
    if (x) {
      setEditingExtraId(x.id);
      setExtraDraft({
        name: x.name,
        price: String(x.price),
        pricing_type: x.pricing_type,
        description: x.description ?? '',
      });
    } else {
      setEditingExtraId(null);
      setExtraDraft(emptyExtra);
    }
    setShowExtraForm(true);
  };
  const closeExtraForm = () => {
    setShowExtraForm(false);
    setEditingExtraId(null);
    setExtraDraft(emptyExtra);
    setExtraError('');
  };
  const submitExtra = () => {
    setExtraError('');
    const name = extraDraft.name.trim();
    if (!name) { setExtraError(t('portal.manage.pricing.extras.errName', 'Indica un nombre para el extra.')); return; }
    const price = Number(extraDraft.price);
    if (!Number.isFinite(price) || price <= 0) {
      setExtraError(t('portal.manage.pricing.extras.errPrice', 'Indica un precio mayor que cero.'));
      return;
    }
    const payload = {
      name,
      price,
      pricing_type: extraDraft.pricing_type,
      description: extraDraft.description.trim() || null,
    };
    const opts = {
      onSuccess: closeExtraForm,
      onError: () => setExtraError(t('portal.manage.pricing.extras.errGeneric', 'No se ha podido guardar el extra.')),
    };
    if (editingExtraId != null) {
      updateExtra.mutate({ id: editingExtraId, ...payload }, opts);
    } else {
      if (!propertyId) return;
      createExtra.mutate({ property_id: propertyId, ...payload }, opts);
    }
  };
  const toggleExtraActive = (x: Extra) =>
    updateExtra.mutate({ id: x.id, active: x.active ? 0 : 1 });
  const removeExtra = async (x: Extra) => {
    if ((await confirm({ title: t('portal.manage.pricing.extras.deleteTitle', 'Eliminar extra'), message: t('portal.manage.pricing.extras.deleteConfirm', '¿Eliminar este extra? Dejará de ofrecerse en nuevas reservas.'), confirmLabel: t('common.deleteYes', 'Sí, eliminar'), tone: 'danger' })) !== 'confirm') return;
    deleteExtra.mutate(x.id);
  };

  if (seasonsLoading || propsLoading || promosLoading || extrasLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-teal-500" size={32} />
      </div>
    );
  }

  const list = seasons ?? [];
  const maxPrice = Math.max(
    1,
    ...list.map((s) => s.price_per_night),
    property?.base_price ?? 0,
  );

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('portal.manage.pricing.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('portal.manage.pricing.subtitle')}
          </p>
        </div>
        <button
          onClick={addSeason}
          disabled={saveSeason.isPending || !hasProperties}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-teal-600 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2.5 transition-colors"
        >
          <Plus size={16} /> {t('portal.manage.pricing.addSeason')}
        </button>
      </div>

      {/* Property selector + bulk ops (agencies) */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <PropertySelector />
        {properties.length > 1 && (
          <button onClick={() => setBulkOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
            <Layers size={15} /> {t('portal.manage.pricing.bulk.button', 'Cambios en lote')}
          </button>
        )}
      </div>

      {bulkOpen && <BulkPricingModal properties={properties} onClose={() => setBulkOpen(false)} />}

      {!hasProperties ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center text-slate-500">
          {t('portal.manage.selector.noPropertyHint', 'Primero crea un alojamiento para configurar sus precios.')}
        </div>
      ) : (
      <>
      {/* Seasons table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900">{t('portal.manage.pricing.seasonsTitle')}</h2>
        </div>

        {list.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-teal-50 text-teal-500">
              <CalendarRange size={24} />
            </div>
            <p className="text-slate-500">{t('portal.manage.pricing.emptyAddFirst')}</p>
            <button
              onClick={addSeason}
              disabled={saveSeason.isPending}
              className="inline-flex items-center gap-1.5 rounded-xl bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2.5 transition-colors"
            >
              <Plus size={16} /> {t('portal.manage.pricing.addSeason')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.colSeason')}</th>
                  <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.colMonths')}</th>
                  <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.colPricePerNight')}</th>
                  <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.colMinStay')}</th>
                  <th className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-right">{t('portal.manage.pricing.colActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((s) => {
                  const isEditing = editingId === s.id && seasonDraft;
                  return (
                    <tr key={s.id} className="hover:bg-slate-50/70 transition-colors">
                      {/* Name */}
                      <td className="px-6 py-4">
                        {isEditing ? (
                          <input
                            value={seasonDraft.name}
                            onChange={(e) =>
                              setSeasonDraft((d) => (d ? { ...d, name: e.target.value } : d))
                            }
                            className="w-44 rounded-lg border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                        ) : (
                          <span className="font-medium text-slate-900">{s.name}</span>
                        )}
                      </td>

                      {/* Month range */}
                      <td className="px-6 py-4 text-slate-600">
                        {isEditing ? (
                          <div className="flex items-center gap-1.5">
                            <select
                              value={seasonDraft.start_month}
                              onChange={(e) =>
                                setSeasonDraft((d) =>
                                  d ? { ...d, start_month: Number(e.target.value) } : d,
                                )
                              }
                              className="rounded-lg border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                              {MONTHS.map((m) => (
                                <option key={m} value={m}>{monthName(m)}</option>
                              ))}
                            </select>
                            <span className="text-slate-400">–</span>
                            <select
                              value={seasonDraft.end_month}
                              onChange={(e) =>
                                setSeasonDraft((d) =>
                                  d ? { ...d, end_month: Number(e.target.value) } : d,
                                )
                              }
                              className="rounded-lg border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                              {MONTHS.map((m) => (
                                <option key={m} value={m}>{monthName(m)}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          monthRange(s)
                        )}
                      </td>

                      {/* Price */}
                      <td className="px-6 py-4">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-500">€</span>
                            <input
                              type="number"
                              value={seasonDraft.price_per_night}
                              onChange={(e) =>
                                setSeasonDraft((d) =>
                                  d ? { ...d, price_per_night: Number(e.target.value) } : d,
                                )
                              }
                              className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                          </div>
                        ) : (
                          <span className="font-semibold text-slate-900">€{s.price_per_night}</span>
                        )}
                      </td>

                      {/* Min stay */}
                      <td className="px-6 py-4">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={seasonDraft.min_stay}
                              onChange={(e) =>
                                setSeasonDraft((d) =>
                                  d ? { ...d, min_stay: Number(e.target.value) } : d,
                                )
                              }
                              className="w-16 rounded-lg border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                            <span className="text-slate-500">{t('portal.manage.pricing.nights')}</span>
                          </div>
                        ) : (
                          <span className="text-slate-600">{s.min_stay} {t('portal.manage.pricing.nights')}</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {isEditing ? (
                            <>
                              <button
                                onClick={() => saveEdit(s.id)}
                                disabled={saveSeason.isPending}
                                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 px-3 py-1.5 text-xs text-white transition-colors"
                              >
                                <Save size={13} /> {t('portal.manage.pricing.save')}
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 px-3 py-1.5 text-xs text-slate-700 transition-colors"
                              >
                                <X size={13} /> {t('portal.manage.pricing.cancel')}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => startEdit(s)}
                                className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 hover:text-teal-700"
                              >
                                <Edit2 size={14} /> {t('portal.manage.pricing.edit')}
                              </button>
                              <button
                                onClick={async () => { if ((await confirm({ title: t('portal.manage.pricing.seasons.deleteTitle', 'Eliminar temporada'), message: t('portal.manage.pricing.seasons.deleteConfirm', '¿Eliminar esta temporada de precios?'), confirmLabel: t('common.deleteYes', 'Sí, eliminar'), tone: 'danger' })) === 'confirm') deleteSeason.mutate(s.id); }}
                                disabled={deleteSeason.isPending}
                                className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-red-600 disabled:opacity-60"
                              >
                                <Trash2 size={14} /> {t('portal.manage.pricing.delete')}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Monthly visualization */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-semibold text-slate-900 mb-5">{t('portal.manage.pricing.monthlyTitle')}</h2>
        <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-2">
          {MONTHS.map((month) => {
            const season = list.find(
              (s) => s.start_month <= month && s.end_month >= month,
            );
            const price = season?.price_per_night ?? property?.base_price ?? 0;
            const pct = Math.max(6, Math.round((price / maxPrice) * 100));
            return (
              <div key={month} className="flex flex-col items-center gap-1.5">
                <div
                  className="w-full rounded-md bg-slate-100 overflow-hidden flex items-end"
                  style={{ height: 72 }}
                >
                  <div
                    className="w-full rounded-md bg-gradient-to-t from-teal-600 to-teal-400 transition-all"
                    style={{ height: `${pct}%` }}
                  />
                </div>
                <div className="text-xs font-medium text-slate-500">
                  {monthName(month).slice(0, 3)}
                </div>
                <div className="text-xs font-bold text-teal-700">€{price}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Base settings */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-semibold text-slate-900">{t('portal.manage.pricing.baseTitle')}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{t('portal.manage.pricing.baseSubtitle')}</p>
          </div>
          {!property ? null : !editingBase ? (
            <button
              onClick={startBaseEdit}
              className="inline-flex items-center gap-1 text-sm font-medium text-teal-600 hover:text-teal-700"
            >
              <Edit2 size={15} /> {t('portal.manage.pricing.edit')}
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={saveBase}
                disabled={saveProperty.isPending}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 px-3 py-1.5 text-sm text-white transition-colors"
              >
                <Save size={14} /> {t('portal.manage.pricing.save')}
              </button>
              <button
                onClick={cancelBaseEdit}
                className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 px-3 py-1.5 text-sm text-slate-700 transition-colors"
              >
                <X size={14} /> {t('portal.manage.pricing.cancel')}
              </button>
            </div>
          )}
        </div>

        {!property ? (
          <p className="text-sm text-slate-500">{t('portal.manage.pricing.baseEmpty')}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Cleaning fee */}
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500 mb-1.5">{t('portal.manage.pricing.cleaningFee')}</div>
              {editingBase && baseDraft ? (
                <div className="flex items-center gap-1">
                  <span className="text-sm text-slate-500">€</span>
                  <input
                    type="number"
                    value={baseDraft.cleaning_fee}
                    onChange={(e) =>
                      setBaseDraft((d) => (d ? { ...d, cleaning_fee: Number(e.target.value) } : d))
                    }
                    className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              ) : (
                <div className="text-lg font-bold text-slate-900">
                  €{(property.cleaning_fee ?? 0).toLocaleString('es-ES')}
                </div>
              )}
            </div>

            {/* Security deposit */}
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500 mb-1.5">{t('portal.manage.pricing.securityDeposit')}</div>
              {editingBase && baseDraft ? (
                <div className="flex items-center gap-1">
                  <span className="text-sm text-slate-500">€</span>
                  <input
                    type="number"
                    value={baseDraft.security_deposit}
                    onChange={(e) =>
                      setBaseDraft((d) => (d ? { ...d, security_deposit: Number(e.target.value) } : d))
                    }
                    className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              ) : (
                <div className="text-lg font-bold text-slate-900">
                  €{(property.security_deposit ?? 0).toLocaleString('es-ES')}
                </div>
              )}
            </div>

            {/* Check-in */}
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500 mb-1.5">{t('portal.manage.pricing.checkIn')}</div>
              {editingBase && baseDraft ? (
                <input
                  type="time"
                  value={baseDraft.check_in_time}
                  onChange={(e) =>
                    setBaseDraft((d) => (d ? { ...d, check_in_time: e.target.value } : d))
                  }
                  className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              ) : (
                <div className="text-lg font-bold text-slate-900">
                  {property.check_in_time ?? '—'}
                </div>
              )}
            </div>

            {/* Check-out */}
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500 mb-1.5">{t('portal.manage.pricing.checkOut')}</div>
              {editingBase && baseDraft ? (
                <input
                  type="time"
                  value={baseDraft.check_out_time}
                  onChange={(e) =>
                    setBaseDraft((d) => (d ? { ...d, check_out_time: e.target.value } : d))
                  }
                  className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              ) : (
                <div className="text-lg font-bold text-slate-900">
                  {property.check_out_time ?? '—'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Length-of-stay discounts */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-teal-50 text-teal-600 shrink-0">
              <Percent size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">{t('portal.manage.pricing.discounts.title', 'Descuentos por estancia')}</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {t('portal.manage.pricing.discounts.subtitle', 'Aplica un descuento automático a estancias largas (sobre el alojamiento, no la limpieza ni la tasa).')}
              </p>
            </div>
          </div>
          {!property ? null : !editingDiscounts ? (
            <button
              onClick={startDiscountEdit}
              className="inline-flex items-center gap-1 text-sm font-medium text-teal-600 hover:text-teal-700 shrink-0"
            >
              <Edit2 size={15} /> {t('portal.manage.pricing.edit')}
            </button>
          ) : (
            <div className="flex gap-2 shrink-0">
              <button
                onClick={saveDiscounts}
                disabled={saveProperty.isPending}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 px-3 py-1.5 text-sm text-white transition-colors"
              >
                <Save size={14} /> {t('portal.manage.pricing.save')}
              </button>
              <button
                onClick={cancelDiscountEdit}
                className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 px-3 py-1.5 text-sm text-slate-700 transition-colors"
              >
                <X size={14} /> {t('portal.manage.pricing.cancel')}
              </button>
            </div>
          )}
        </div>

        {!property ? (
          <p className="text-sm text-slate-500">{t('portal.manage.pricing.baseEmpty')}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Weekly */}
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500 mb-1.5">{t('portal.manage.pricing.discounts.weekly', 'Descuento semanal (≥ 7 noches)')}</div>
              {editingDiscounts && discountDraft ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number" min={0} max={100}
                    value={discountDraft.weekly}
                    onChange={(e) => setDiscountDraft((d) => (d ? { ...d, weekly: Number(e.target.value) } : d))}
                    className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <span className="text-sm text-slate-500">%</span>
                </div>
              ) : (
                <div className="text-lg font-bold text-slate-900">{property.discount_weekly_percent ?? 0}%</div>
              )}
            </div>
            {/* Monthly */}
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs text-slate-500 mb-1.5">{t('portal.manage.pricing.discounts.monthly', 'Descuento mensual (≥ 28 noches)')}</div>
              {editingDiscounts && discountDraft ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number" min={0} max={100}
                    value={discountDraft.monthly}
                    onChange={(e) => setDiscountDraft((d) => (d ? { ...d, monthly: Number(e.target.value) } : d))}
                    className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <span className="text-sm text-slate-500">%</span>
                </div>
              ) : (
                <div className="text-lg font-bold text-slate-900">{property.discount_monthly_percent ?? 0}%</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Promo codes */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-teal-50 text-teal-600 shrink-0">
              <Tag size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">{t('portal.manage.pricing.promo.title', 'Códigos promocionales')}</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {t('portal.manage.pricing.promo.subtitle', 'Crea códigos de descuento para compartir con tus huéspedes.')}
              </p>
            </div>
          </div>
          {!showPromoForm && (
            <button
              onClick={() => { setShowPromoForm(true); setPromoError(''); }}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium px-4 py-2.5 transition-colors"
            >
              <Plus size={16} /> {t('portal.manage.pricing.promo.add', 'Nuevo código')}
            </button>
          )}
        </div>

        {/* Create form */}
        {showPromoForm && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-5 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.pricing.promo.code', 'Código')}</label>
                <input
                  value={promoDraft.code}
                  onChange={(e) => setPromoDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
                  placeholder="VERANO10"
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.pricing.promo.scope', 'Aplica a')}</label>
                <select
                  value={promoDraft.scope}
                  onChange={(e) => setPromoDraft((d) => ({ ...d, scope: e.target.value as 'property' | 'all' }))}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="property">{t('portal.manage.pricing.promo.scopeProperty', 'Este alojamiento')}</option>
                  <option value="all">{t('portal.manage.pricing.promo.scopeAll', 'Todos mis alojamientos')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.pricing.promo.kind', 'Tipo de descuento')}</label>
                <select
                  value={promoDraft.kind}
                  onChange={(e) => setPromoDraft((d) => ({ ...d, kind: e.target.value as 'percent' | 'fixed' }))}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="percent">{t('portal.manage.pricing.promo.kindPercent', 'Porcentaje (%)')}</option>
                  <option value="fixed">{t('portal.manage.pricing.promo.kindFixed', 'Importe fijo (€)')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  {promoDraft.kind === 'percent'
                    ? t('portal.manage.pricing.promo.amountPercent', 'Descuento (%)')
                    : t('portal.manage.pricing.promo.amountFixed', 'Descuento (€)')}
                </label>
                <input
                  type="number" min={1}
                  value={promoDraft.amount}
                  onChange={(e) => setPromoDraft((d) => ({ ...d, amount: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.pricing.promo.validFrom', 'Válido desde (opcional)')}</label>
                <input
                  type="date"
                  value={promoDraft.valid_from}
                  onChange={(e) => setPromoDraft((d) => ({ ...d, valid_from: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.pricing.promo.validUntil', 'Válido hasta (opcional)')}</label>
                <input
                  type="date"
                  value={promoDraft.valid_until}
                  onChange={(e) => setPromoDraft((d) => ({ ...d, valid_until: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.pricing.promo.maxUses', 'Usos máximos (opcional)')}</label>
                <input
                  type="number" min={1}
                  value={promoDraft.max_uses}
                  onChange={(e) => setPromoDraft((d) => ({ ...d, max_uses: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            {promoError && <p className="text-sm text-rose-600">{promoError}</p>}
            <div className="flex gap-2">
              <button
                onClick={submitPromo}
                disabled={createPromo.isPending}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 px-3 py-1.5 text-sm text-white transition-colors"
              >
                <Save size={14} /> {t('portal.manage.pricing.save')}
              </button>
              <button
                onClick={() => { setShowPromoForm(false); setPromoDraft(emptyPromo); setPromoError(''); }}
                className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 px-3 py-1.5 text-sm text-slate-700 transition-colors"
              >
                <X size={14} /> {t('portal.manage.pricing.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* List */}
        {(promos?.length ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">{t('portal.manage.pricing.promo.empty', 'Aún no has creado ningún código promocional.')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.promo.code', 'Código')}</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.promo.discount', 'Descuento')}</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.promo.scope', 'Aplica a')}</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.promo.uses', 'Usos')}</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider text-right">{t('portal.manage.pricing.colActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(promos ?? []).map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="px-4 py-3 font-mono font-semibold text-slate-900">{p.code}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {p.discount_percent ? `${p.discount_percent}%` : `€${p.discount_fixed}`}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {p.property_id == null
                        ? t('portal.manage.pricing.promo.scopeAll', 'Todos mis alojamientos')
                        : t('portal.manage.pricing.promo.scopeProperty', 'Este alojamiento')}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {p.used_count}{p.max_uses ? ` / ${p.max_uses}` : ''}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={async () => { if ((await confirm({ title: t('portal.manage.pricing.promos.deleteTitle', 'Eliminar código'), message: t('portal.manage.pricing.promos.deleteConfirm', '¿Eliminar este código promocional?'), confirmLabel: t('common.deleteYes', 'Sí, eliminar'), tone: 'danger' })) === 'confirm') deletePromo.mutate(p.id); }}
                        disabled={deletePromo.isPending}
                        className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-red-600 disabled:opacity-60"
                      >
                        <Trash2 size={14} /> {t('portal.manage.pricing.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paid extras */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-teal-50 text-teal-600 shrink-0">
              <ShoppingBag size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">{t('portal.manage.pricing.extras.title', 'Extras de pago')}</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {t('portal.manage.pricing.extras.subtitle', 'Servicios opcionales que el huésped puede añadir a su reserva (cuna, parking, check-in tardío…).')}
              </p>
            </div>
          </div>
          {!showExtraForm && (
            <button
              onClick={() => openExtraForm()}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium px-4 py-2.5 transition-colors"
            >
              <Plus size={16} /> {t('portal.manage.pricing.extras.add', 'Nuevo extra')}
            </button>
          )}
        </div>

        {/* Add / edit form */}
        {showExtraForm && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-5 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.pricing.extras.name', 'Nombre')}</label>
                <input
                  value={extraDraft.name}
                  onChange={(e) => setExtraDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder={t('portal.manage.pricing.extras.namePlaceholder', 'Cuna para bebé')}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.pricing.extras.price', 'Precio (€)')}</label>
                <input
                  type="number" min={0} step="0.01"
                  value={extraDraft.price}
                  onChange={(e) => setExtraDraft((d) => ({ ...d, price: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.pricing.extras.pricingType', 'Se cobra')}</label>
                <select
                  value={extraDraft.pricing_type}
                  onChange={(e) => setExtraDraft((d) => ({ ...d, pricing_type: e.target.value as ExtraPricingType }))}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="per_stay">{pricingTypeLabels.per_stay}</option>
                  <option value="per_night">{pricingTypeLabels.per_night}</option>
                  <option value="per_guest">{pricingTypeLabels.per_guest}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{t('portal.manage.pricing.extras.description', 'Descripción (opcional)')}</label>
                <input
                  value={extraDraft.description}
                  onChange={(e) => setExtraDraft((d) => ({ ...d, description: e.target.value }))}
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            {extraError && <p className="text-sm text-rose-600">{extraError}</p>}
            <div className="flex gap-2">
              <button
                onClick={submitExtra}
                disabled={createExtra.isPending || updateExtra.isPending}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 px-3 py-1.5 text-sm text-white transition-colors"
              >
                <Save size={14} /> {t('portal.manage.pricing.save')}
              </button>
              <button
                onClick={closeExtraForm}
                className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 px-3 py-1.5 text-sm text-slate-700 transition-colors"
              >
                <X size={14} /> {t('portal.manage.pricing.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* List */}
        {(extras?.length ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">{t('portal.manage.pricing.extras.empty', 'Aún no has creado ningún extra para este alojamiento.')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.extras.name', 'Nombre')}</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.extras.price', 'Precio (€)')}</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.extras.pricingType', 'Se cobra')}</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">{t('portal.manage.pricing.extras.active', 'Estado')}</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider text-right">{t('portal.manage.pricing.colActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(extras ?? []).map((x) => (
                  <tr key={x.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{x.name}</div>
                      {x.description && <div className="text-xs text-slate-400 mt-0.5">{x.description}</div>}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">€{x.price.toLocaleString('es-ES')}</td>
                    <td className="px-4 py-3 text-slate-600">{pricingTypeLabels[x.pricing_type] ?? x.pricing_type}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleExtraActive(x)}
                        disabled={updateExtra.isPending}
                        title={x.active
                          ? t('portal.manage.pricing.extras.deactivate', 'Desactivar (dejará de ofrecerse)')
                          : t('portal.manage.pricing.extras.activate', 'Activar')}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-60 ${
                          x.active
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                            : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'
                        }`}
                      >
                        {x.active
                          ? t('portal.manage.pricing.extras.activeYes', 'Activo')
                          : t('portal.manage.pricing.extras.activeNo', 'Inactivo')}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openExtraForm(x)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 hover:text-teal-700"
                        >
                          <Edit2 size={14} /> {t('portal.manage.pricing.edit')}
                        </button>
                        <button
                          onClick={() => removeExtra(x)}
                          disabled={deleteExtra.isPending}
                          className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-red-600 disabled:opacity-60"
                        >
                          <Trash2 size={14} /> {t('portal.manage.pricing.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

/* ── Bulk pricing operations (agencies) ─────────────────────────────────────── */
type BulkOpKind = BulkOp['op'];

function BulkPricingModal({ properties, onClose }: { properties: Property[]; onClose: () => void }) {
  const { t } = useTranslation();
  const bulk = useBulkProperties();
  const [ids, setIds] = useState<number[]>(() => properties.map(p => p.id));
  const [op, setOp] = useState<BulkOpKind>('price_pct');
  const [value, setValue] = useState('10');
  const [season, setSeason] = useState({ name: 'Temporada alta', start_month: 6, end_month: 9, price_per_night: 150, min_stay: 3 });
  const [done, setDone] = useState<number | null>(null);

  const all = ids.length === properties.length;
  const toggleId = (id: number) => setIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const input = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';

  const apply = () => {
    if (!ids.length) return;
    const payload: BulkOp = op === 'add_season'
      ? { ids, op, season }
      : { ids, op, value: op === 'status' ? value : Number(value) };
    bulk.mutate(payload, { onSuccess: (r) => setDone(r.affected) });
  };

  const OPS: { key: BulkOpKind; label: string }[] = [
    { key: 'price_pct', label: t('portal.manage.pricing.bulk.opPct', 'Ajustar precio base (%)') },
    { key: 'price_set', label: t('portal.manage.pricing.bulk.opSet', 'Fijar precio base (€)') },
    { key: 'min_nights', label: t('portal.manage.pricing.bulk.opMin', 'Noches mínimas') },
    { key: 'status', label: t('portal.manage.pricing.bulk.opStatus', 'Estado (activar/pausar)') },
    { key: 'add_season', label: t('portal.manage.pricing.bulk.opSeason', 'Añadir temporada a varios') },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Layers size={18} className="text-teal-600" /> {t('portal.manage.pricing.bulk.title', 'Cambios en lote')}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {done !== null ? (
          <div className="p-8 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><Check size={26} /></div>
            <p className="mt-3 font-semibold text-slate-800">{t('portal.manage.pricing.bulk.done', 'Aplicado a {{count}} alojamientos', { count: done })}</p>
            <button onClick={onClose} className="mt-5 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg">{t('portal.manage.pricing.bulk.close', 'Cerrar')}</button>
          </div>
        ) : (
        <div className="p-5 space-y-4">
          {/* Operation */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">{t('portal.manage.pricing.bulk.operation', 'Operación')}</label>
            <select value={op} onChange={e => setOp(e.target.value as BulkOpKind)} className={input}>
              {OPS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>

          {/* Value / season inputs */}
          {op === 'status' ? (
            <select value={value} onChange={e => setValue(e.target.value)} className={input}>
              <option value="active">{t('portal.manage.properties.statusActive', 'Activo')}</option>
              <option value="paused">{t('portal.manage.properties.statusPaused', 'Pausado')}</option>
            </select>
          ) : op === 'add_season' ? (
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2 text-sm"><span className="block text-xs font-semibold text-slate-500 mb-1">{t('portal.manage.pricing.seasonName', 'Nombre')}</span><input className={input} value={season.name} onChange={e => setSeason(s => ({ ...s, name: e.target.value }))} /></label>
              <label className="text-sm"><span className="block text-xs font-semibold text-slate-500 mb-1">{t('portal.manage.pricing.bulk.fromMonth', 'Mes inicio')}</span><select className={input} value={season.start_month} onChange={e => setSeason(s => ({ ...s, start_month: Number(e.target.value) }))}>{MONTHS.map(m => <option key={m} value={m}>{t(`portal.manage.pricing.months.${m}`, String(m))}</option>)}</select></label>
              <label className="text-sm"><span className="block text-xs font-semibold text-slate-500 mb-1">{t('portal.manage.pricing.bulk.toMonth', 'Mes fin')}</span><select className={input} value={season.end_month} onChange={e => setSeason(s => ({ ...s, end_month: Number(e.target.value) }))}>{MONTHS.map(m => <option key={m} value={m}>{t(`portal.manage.pricing.months.${m}`, String(m))}</option>)}</select></label>
              <label className="text-sm"><span className="block text-xs font-semibold text-slate-500 mb-1">€/noche</span><input type="number" className={input} value={season.price_per_night} onChange={e => setSeason(s => ({ ...s, price_per_night: Number(e.target.value) }))} /></label>
              <label className="text-sm"><span className="block text-xs font-semibold text-slate-500 mb-1">{t('portal.manage.pricing.minStay', 'Estancia mínima')}</span><input type="number" className={input} value={season.min_stay} onChange={e => setSeason(s => ({ ...s, min_stay: Number(e.target.value) }))} /></label>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                {op === 'price_pct' ? t('portal.manage.pricing.bulk.percent', 'Porcentaje (+ sube / − baja)') : op === 'price_set' ? t('portal.manage.pricing.bulk.newPrice', 'Nuevo precio (€)') : t('portal.manage.pricing.bulk.nights', 'Noches')}
              </label>
              <input type="number" className={input} value={value} onChange={e => setValue(e.target.value)} />
            </div>
          )}

          {/* Property multi-select */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-slate-500">{t('portal.manage.pricing.bulk.applyTo', 'Aplicar a')} ({ids.length}/{properties.length})</label>
              <button onClick={() => setIds(all ? [] : properties.map(p => p.id))} className="text-xs text-teal-700 font-medium hover:underline">
                {all ? t('portal.manage.pricing.bulk.none', 'Ninguno') : t('portal.manage.pricing.bulk.all', 'Todos')}
              </button>
            </div>
            <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
              {properties.map(p => (
                <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={ids.includes(p.id)} onChange={() => toggleId(p.id)} className="rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                  <span className="truncate flex-1">{p.name || `#${p.id}`}</span>
                  {p.base_price != null && <span className="text-xs text-slate-400">€{p.base_price}</span>}
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">{t('portal.manage.pricing.bulk.cancel', 'Cancelar')}</button>
            <button onClick={apply} disabled={!ids.length || bulk.isPending} className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold disabled:opacity-50">
              {bulk.isPending ? '…' : t('portal.manage.pricing.bulk.apply', 'Aplicar a {{count}}', { count: ids.length })}
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
